import { useRef, useEffect } from 'react';
import { PollIcon, GameIcon } from '../Icons';

export interface ChatAttachMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onPoll: () => void;
  onGameInvite: () => void;
  gameInviteTitle?: string;
  gameInviteSubtitle?: string;
  gameInviteDisabled?: boolean;
  pollDisabled?: boolean;
  onCiaRdv?: () => void;
  ciaRdvTitle?: string;
  ciaRdvSubtitle?: string;
}

export default function ChatAttachMenu({
  isOpen,
  onClose,
  anchorRef,
  onPoll,
  onGameInvite,
  gameInviteTitle = 'Inviter à jouer',
  gameInviteSubtitle = 'Proposez au groupe de rejoindre une table',
  gameInviteDisabled = false,
  pollDisabled = true,
  onCiaRdv,
  ciaRdvTitle = 'Rendez-vous défi CIA',
  ciaRdvSubtitle = 'Proposer une date pour votre défi',
}: ChatAttachMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target as Node))
      ) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  return (
    <div className="chat-attach-menu" ref={menuRef}>
      <button
        type="button"
        className="chat-attach-menu-item"
        disabled={pollDisabled}
        onClick={() => {
          onPoll();
          onClose();
        }}
      >
        <span className="chat-attach-menu-icon poll">
          <PollIcon size={22} />
        </span>
        <span className="chat-attach-menu-text">
          <span className="chat-attach-menu-title">Sondage</span>
          <span className="chat-attach-menu-subtitle">
            {pollDisabled ? 'Bientôt disponible' : 'Posez une question au groupe'}
          </span>
        </span>
      </button>

      <button
        type="button"
        className="chat-attach-menu-item"
        disabled={gameInviteDisabled}
        onClick={() => {
          onGameInvite();
          onClose();
        }}
      >
        <span className="chat-attach-menu-icon game">
          <GameIcon size={22} />
        </span>
        <span className="chat-attach-menu-text">
          <span className="chat-attach-menu-title">{gameInviteTitle}</span>
          <span className="chat-attach-menu-subtitle">{gameInviteSubtitle}</span>
        </span>
      </button>

      {onCiaRdv && (
        <button
          type="button"
          className="chat-attach-menu-item"
          onClick={() => {
            onCiaRdv();
            onClose();
          }}
        >
          <span className="chat-attach-menu-icon cia">
            <span aria-hidden="true">🏆</span>
          </span>
          <span className="chat-attach-menu-text">
            <span className="chat-attach-menu-title">{ciaRdvTitle}</span>
            <span className="chat-attach-menu-subtitle">{ciaRdvSubtitle}</span>
          </span>
        </button>
      )}
    </div>
  );
}
